<script setup>
import {computed, ref, watch} from 'vue';
import {useRoute} from 'vue-router';
import {useStore} from "vuex";
import EODDateButton from "@components/reports/Manager/EODDateButton.vue";
import axios from "axios";

const store = useStore();
const shops = computed(() => [...store.state.shops]); // Spread for type casting
const dates = computed(() => [...store.state.dates]); // same

// <editor-fold desc="Getting submissions">-------------------------------

const submissions = ref([]);
const getEODsSubmitted = async (dateStart, dateEnd, shop_id) => {

  // Format Dates
  const date_start = dateStart instanceof Date ? dateStart.toISOString().split('T')[0] : dateStart;
  const date_end = dateEnd instanceof Date ? dateEnd.toISOString().split('T')[0] : dateEnd;

  // Send Request
  const response = await axios.get(`${localized.baseURL}reports/eods-submitted`, {
    params: {date_start, date_end, shop_id}
  })

  if (response.status === 200) {
    console.log("Response.data: ", response.data);
    return response.data;
  } else {
    console.error("Error fetching EODs submitted: ", response);
  }
}
watch(dates, async (newDates) => {
  if (newDates.length > 0) {
    const dateStart = new Date(newDates[0]);
    const dateEnd = new Date(newDates[newDates.length - 1]);
    const eods = await getEODsSubmitted(dateStart, dateEnd);
    submissions.value = eods;
  }
}, {immediate: true});

// </editor-fold>---------------------------------------------------------

// <editor-fold desc="Table Styling">-------------------------------------

const weekdays = ['Su', 'M', 'T', 'W', 'Th', 'F', 'S'];
const rowHeaderWidth = 'w-40';
const bodyColWidth = 'min-w-5 w-6';

// Filler Days

// Determine number of filler days
const firstDate = dates.value[0]; // Get the first date from the dates array
const weekdayOffset = firstDate.getDay(); // Get the weekday offset of the first date
const fillerDays = computed(() => {

  if (!dates.value.length) return [];

  // Get the number of days in the selected month
  const maxFillerDays = new Date(
      firstDate.getFullYear(),
      firstDate.getMonth() + 1, // Set the month to next month
      0 // and check 0 index, which is the last day of the previous month
  ).getDate(); // Get the number of days in that month by simply getting date of last day
  let days = [];
  for (let i = 1; i <= maxFillerDays; i++) {
    days.push(i);
  }

  // Subtract the days in the month already from the filler days
  return days.slice(dates.value.length, -1);
});

// </editor-fold>---------------------------------------------------------

// <editor-fold desc="Hover Effect">-------------------------------

const hoveredCol = ref(null);
const hoverColumn = (index) => {
  hoveredCol.value = index;
}
const unhoverColumn = () => {
  hoveredCol.value = null;
}

const hoveredRow = ref(null);
const hoverRow = (index) => {
  hoveredRow.value = index;
}
const unhoverRow = () => {
  hoveredRow.value = null;
}

// </editor-fold>----------------------------------------------------------

// <editor-fold desc="Collapse Toggle">-------------------------------

const panelToggleButton = ref(null);
const togglePanel = () => {
  if (panelToggleButton.value) {
    panelToggleButton.value.$el.click();
  }
}
const route = useRoute();
//
const initialCollapsed = route.params['shopId'] !== undefined;

// </editor-fold>-----------------------------------------------------
</script>

<template>
  <Panel header="Select EOD Report to View" class="max-w-[1280px] w-full" toggleable :collapsed="initialCollapsed">
    <template #header>
      <span class="p-panel-title text-left w-full cursor-pointer" @click="togglePanel">Select EOD Report to View</span>
    </template>
    <template #togglebutton="{toggleCallback, collapsed}">
      <Button ref="panelToggleButton" :icon="{'pi pi-minus' : !collapsed, 'pi pi-plus': collapsed}" variant="text"
              rounded severity="secondary" @click="toggleCallback"/>
    </template>
    <div class="flex w-full justify-center">
      <div class="flex gap-0 w-fit">
        <div class="flex flex-col">
          <br/><br/>


          <div v-for="(s, index) in shops"
               :key="index"
               :class="[`text-center overflow-hidden mx-0 rounded-l ease-in-out duration-50 ${rowHeaderWidth}`, { 'bg-slate-100': hoveredRow === index }]">
            {{ s.name }}
          </div>
        </div>
        <div class="flex flex-col w-full overflow-auto">

          <!--Number day of month-->
          <div data-test="dates-header" class="flex">

            <!--"Real" Dates-->
            <div v-for="(d, index) in dates"
                 :key="index"
                 :class="[`text-center overflow-hidden mx-1 rounded-t ${bodyColWidth}`, { 'bg-slate-100': hoveredCol === index }]"
                 @mouseover="hoverColumn(index)"
                 @mouseleave="unhoverColumn">
              {{ d.getDate() }}
            </div>

            <!--Filler Days Implementation-->
            <div v-for="(d, index) in fillerDays"
                 :class="`${bodyColWidth} text-center mx-1 overflow-hidden text-slate-300`"
                 :key="`filler-${index}`">
              {{ d }}
            </div>
          </div>
          <!--Day of the Week-->
          <div data-test="weekdays-header" class="flex">

            <!--Real Dates-->
            <div v-for="(d, index) in dates"
                 :key="index"
                 :class="[`text-center overflow-hidden mx-1 ${bodyColWidth}`, { 'bg-slate-100': hoveredCol === index }]"
                 @mouseover="hoverColumn(index)"
                 @mouseleave="unhoverColumn">
              {{ weekdays[d.getDay()] }}
            </div>

            <!--Filler days-->
            <div v-for="(d, index) in fillerDays"
                 :key="`filler-${index}`"
                 :class="`text-center overflow-hidden mx-1 ${bodyColWidth} text-slate-300`">
              {{ weekdays[((d % 7) + weekdayOffset) > 6 ? ((d % 7) + weekdayOffset) - 7 : ((d % 7) + weekdayOffset) ] }}
            </div>

          </div>

          <!--EOD Date Buttons-->
          <div class="flex gap-0">

            <!--EOD Date Buttons - Real Dates-->
            <div class="flex flex-col">

              <!--A row for each store-->
              <div data-test="eod-table"
                   class="flex items-center hover:bg-slate-100 border-0 rounded-r ease-in-out duration-50"
                   v-for="(s, sIndex) in shops"
                   :key="s.id"
                   @mouseover="hoverRow(sIndex)"
                   @mouseleave="unhoverRow(sIndex)">

                <!--A column within each row for each day -->
                <div v-for="(d, dIndex) in dates"
                     :key="dIndex"
                     :class="[`text-center my-0 mx-1 h-full overflow-hidden ${bodyColWidth}`, { 'bg-slate-100': hoveredCol === dIndex, 'rounded-b' : sIndex === shops.length - 1 }]"
                     @mouseover="hoverColumn(dIndex)"
                     @mouseleave="unhoverColumn(dIndex)">
                  <EODDateButton :date="d" :shopId="s.id" :submissions="submissions[s.id]"
                                 :key="`${JSON.stringify(d)}${s.id}`" :collapseNavFn="togglePanel"/>
                </div>
              </div>

            </div>

            <!--EOD Date Buttons - Filler Days-->
            <div class="flex flex-col">

              <!--A row for each store-->
              <div data-test="eod-table"
                   class="flex items-center"
                   v-for="s in shops"
                   :key="s.id">

                <!--A column within each row for each day -->
                <div v-for="(d, dIndex) in fillerDays"
                     :key="dIndex"
                     :class="`text-center my-0 mx-1 h-full overflow-hidden ${bodyColWidth}`">
                  <EODDateButton :active="false" :date="d" :shopId="s.id" :submissions="submissions[s.id]"
                                 :key="`${JSON.stringify(d)}${s.id}`" :collapseNavFn="togglePanel"/>
                </div>
              </div>

            </div>

          </div>

        </div>
      </div>
    </div>
  </Panel>
</template>